/**
 * CRM tab hooks (prove-out read surfaces). All server state through React Query;
 * queryKeys namespaced ['crm', ...] (distinct from the Admin panel's ['crm-admin', ...]
 * so tab invalidations never collide with the admin caches). Conservative staleTimes,
 * no polling — the Azure SQL tier saturates on chatty dashboards (prod 2026-06-30).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { ApiError, apiFetch, apiFetchBlob, apiFetchText } from '../api/client';
import { useEffectiveUser } from '../auth/useAuthUser';
import type {
  CrmAcceptRecordResponse,
  CrmActivityTypesResponse,
  CrmAddEmploymentResult,
  CrmAddMemberResult,
  CrmAttrHistoryResponse,
  CrmConnectionsResponse,
  CrmDealMergePreview,
  CrmDealMergeTarget,
  CrmEmailContent,
  CrmEmailMeta,
  CrmEmailParticipantsResponse,
  CrmEmploymentResponse,
  CrmEntityProfile,
  CrmEvidenceBodyResponse,
  CrmEvidenceLinkTargetRef,
  CrmEvidenceLinkTargetsResponse,
  CrmFactEvidenceResponse,
  CrmFactInterpretResponse,
  CrmFactNoteWriteResponse,
  CrmFieldHistoryResponse,
  CrmGridRequest,
  CrmGridResponse,
  CrmLinkedRecordsResponse,
  CrmLinkEmailToRecordResponse,
  CrmListOptionsResponse,
  CrmListShare,
  CrmListSharesResponse,
  CrmListsResponse,
  CrmListSummary,
  CrmListTemplatesResponse,
  CrmLookupResponse,
  CrmMeetingAttendeesResponse,
  CrmMemberLookupResponse,
  CrmMergeCandidatesResponse,
  CrmMergeHistoryResponse,
  CrmNote,
  CrmNoteListResponse,
  CrmOrgCreateResponse,
  CrmOrgPeopleResponse,
  CrmPersonCard,
  CrmPortcoLinkResponse,
  CrmRecordEvidenceRefuteResponse,
  CrmRecordEvidenceResponse,
  CrmRecordLayout,
  CrmRecordLayoutSection,
  CrmRecordResponse,
  CrmRecordReviewV2Response,
  CrmSearchResponse,
  CrmShareCandidatesResponse,
  CrmShareGrantResponse,
  CrmShareRevokeResponse,
  CrmTimelineResponse,
  CrmView,
  CrmViewsResponse,
  RecordReviewCas,
} from '../types';

export type CrmEntityKind = 'person' | 'organization';

const kindPath = (kind: CrmEntityKind) => (kind === 'person' ? 'person' : 'organization');

/**
 * Governed search, one category per hook call. POST body but semantically a read —
 * modeled as useQuery keyed on the (already-debounced) query string so results are
 * URL-restorable and cached across selection open/close.
 */
export function useCrmSearch(searchKind: 'directory' | 'content', q: string) {
  const query = q.trim();
  return useQuery<CrmSearchResponse>({
    queryKey: ['crm', 'search', searchKind, query],
    queryFn: () =>
      apiFetch<CrmSearchResponse>('/api/crm/search', {
        method: 'POST',
        body: JSON.stringify({ query, kind: searchKind, limit: 50 }),
      }),
    enabled: query.length >= 2,
    staleTime: 60_000,
  });
}

/**
 * Opportunity list-record names for the search page (search-quality spec Part 3). Reuses the
 * governed ref-picker lookup — list-visibility (owner/share overlay) + the D8 proposed-row
 * audience gate live in SQL, which is exactly why entry names are NOT in the Meili index.
 */
export function useCrmOpportunitySearch(q: string) {
  const query = q.trim();
  return useQuery<CrmLookupResponse>({
    queryKey: ['crm', 'search', 'opportunity', query],
    queryFn: () =>
      apiFetch<CrmLookupResponse>('/api/crm/lookup', {
        method: 'POST',
        body: JSON.stringify({ kind: 'opportunity', q: query, limit: 50 }),
      }),
    enabled: query.length >= 2,
    staleTime: 60_000,
    // The search page holds its entity groups until this lookup settles (best-match ordering), so a
    // deterministic 4xx (e.g. a caller without CRM-privileged access) must fail at once rather than
    // wait out the global retry; a 5xx / network blip still gets its one retry.
    retry: (failures, err) => failures < 1 && !(err instanceof ApiError && err.status < 500),
  });
}

/** The Lists picker — every list the caller is entitled to see + its scoped entry count. */
export function useCrmLists() {
  return useQuery<CrmListsResponse>({
    queryKey: ['crm', 'lists'],
    queryFn: () => apiFetch<CrmListsResponse>('/api/crm/lists'),
    staleTime: 60_000,
  });
}

/**
 * Server-side grid page over one list (the vendor-neutral getRows contract). POST body but a
 * read; keyed on the full request so paging/sort/filter/search each produce a distinct cache
 * entry (React Query hashes the key deterministically). `keepPreviousData`-free by design — the
 * conservative CRM staleTime + explicit key changes keep it predictable on the Azure SQL tier.
 */
export function useCrmGrid(listId: number | null, req: CrmGridRequest) {
  return useQuery<CrmGridResponse>({
    queryKey: ['crm', 'grid', listId, req],
    queryFn: () =>
      apiFetch<CrmGridResponse>(`/api/crm/lists/${listId}/grid`, {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    enabled: listId != null,
    staleTime: 60_000,
  });
}

/**
 * Item D (D2b): the single-record read for the record panel — the resolved row + record_field_states.v1
 * (GET /crm/lists/{id}/entries/{id}/record). The panel renders instantly from the loaded grid row (R3),
 * then hydrates from this for authoritative, actionable field state. Invalidated by the same field
 * mutations as the grid so panel + grid stay in lockstep.
 */
export function useCrmRecord(listId: number | null, entryId: number | null) {
  return useQuery<CrmRecordResponse>({
    queryKey: ['crm', 'record', listId, entryId],
    queryFn: () =>
      apiFetch<CrmRecordResponse>(`/api/crm/lists/${listId}/entries/${entryId}/record`),
    enabled: listId != null && entryId != null,
    staleTime: 60_000,
  });
}

/** record_review.v2 — the governed field-review read model (R1). DARK: the endpoint 404s until
 * settings.crm_record_review_v2_enabled is flipped, so this stays a read-only SHADOW beside the
 * authoritative useCrmRecord (v1) until R5-FE/R4 migrate the panel onto it. `retry:false` so a 404
 * (flag off / not-visible) surfaces immediately rather than retrying. Pair with adaptRecordReviewV2
 * (components/crm/recordReviewV2) to map the payload into the panel's CrmRecordFieldState shape. */
export function useCrmRecordV2(listId: number | null, entryId: number | null, enabled = true) {
  return useQuery<CrmRecordReviewV2Response>({
    queryKey: ['crm', 'record-v2', listId, entryId],
    queryFn: () =>
      apiFetch<CrmRecordReviewV2Response>(`/api/crm/lists/${listId}/entries/${entryId}/record/v2`),
    enabled: enabled && listId != null && entryId != null,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * WP1 / S1.2 — the record panel's PARENT PORTFOLIO COMPANY section (portco-transactions spec §2.2).
 *
 * Readable by anyone who can see the deal; `editable` on the payload says whether THIS caller may
 * change it (deal team, or the audited full_deal_access capability), and the picker's `candidates`
 * ship only when it is true — so the component never has to re-derive the permission.
 *
 * ⚠ `links` is an ARRAY and `conflict` may be true. A merge of two linked deals fronts two parents,
 * which spec invariant 1 calls a data error to SHOW rather than resolve; rendering `links[0]` would
 * hide it. Lazy (`enabled`) like the evidence read — the section is not always open.
 */
export function useCrmPortcoLink(listId: number | null, entryId: number | null, enabled = true) {
  return useQuery<CrmPortcoLinkResponse>({
    queryKey: ['crm', 'portco-link', listId, entryId],
    queryFn: () =>
      apiFetch<CrmPortcoLinkResponse>(`/api/crm/lists/${listId}/entries/${entryId}/portco-link`),
    enabled: enabled && listId != null && entryId != null,
    staleTime: 60_000,
  });
}

/** Set this deal's parent portfolio company (provenance `operator` — the backfill never overwrites
 *  it). 403 when the caller is not on the deal team. Naming a DIFFERENT company is a correction and
 *  succeeds: the panel is the door built to fix a mis-parented deal, unlike the backfill, which is
 *  refused a re-parent because a machine doing it silently is invisible. */
export function useSetPortcoLink(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, portfolioCompanyId, linkKind }:
                 { entryId: number; portfolioCompanyId: number; linkKind: string }) =>
      apiFetch<CrmPortcoLinkResponse>(`/api/crm/lists/${listId}/entries/${entryId}/portco-link`, {
        method: 'PUT',
        body: JSON.stringify({ portfolio_company_id: portfolioCompanyId, link_kind: linkKind }),
      }),
    onSettled: (_d, _e, vars) =>
      qc.invalidateQueries({ queryKey: ['crm', 'portco-link', listId, vars.entryId] }),
  });
}

/** Clear this deal's parent link. Withdraws no inherited domain — see the endpoint's docstring. */
export function useClearPortcoLink(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId }: { entryId: number }) =>
      apiFetch<CrmPortcoLinkResponse>(`/api/crm/lists/${listId}/entries/${entryId}/portco-link`,
        { method: 'DELETE' }),
    onSettled: (_d, _e, vars) =>
      qc.invalidateQueries({ queryKey: ['crm', 'portco-link', listId, vars.entryId] }),
  });
}

/** Raw evidence timeline for a deal record (proposed candidate OR committed opp) — the emails/meetings
 * the classifier bound to it. D8-gated + per-mailbox §9-gated on the server (the CRM-governed
 * /evidence endpoint, NOT the ungoverned Deals endpoint). `enabled` gates the fetch to when the
 * Evidence sub-view is actually open (lazy).
 *
 * WP-7 — `party` is the lever-F filter: node ids (`org:12` / `person:34`), repeatable, and IN THE
 * QUERY KEY, because two different selections are two different governed reads and must not serve
 * each other's cache. The client sends only `party`: the timeline's own date range stays a
 * client-side split over the three merged lanes, and narrowing the evidence lane on the server would
 * make its "+N older than this range" escape undercount rows the range is holding back.
 *
 * Sorted before it goes into the key so ['org:2','org:1'] and ['org:1','org:2'] are ONE cache entry
 * and one request — the chip row is a set, and the order the operator clicked in is not part of the
 * question. */
export function useCrmRecordEvidence(listId: number | null, entryId: number | null, enabled = true,
                                     party: string[] = []) {
  const ids = [...party].sort();
  const qs = ids.map((id) => `party=${encodeURIComponent(id)}`).join('&');
  return useQuery<CrmRecordEvidenceResponse>({
    queryKey: ['crm', 'record-evidence', listId, entryId, ids],
    queryFn: () =>
      apiFetch<CrmRecordEvidenceResponse>(
        `/api/crm/lists/${listId}/entries/${entryId}/evidence${qs ? `?${qs}` : ''}`),
    enabled: enabled && listId != null && entryId != null,
    staleTime: 60_000,
    // ⛔ WP-7 — the previous payload stands while the next one loads, and it is the CHIP ROW that
    // needs it: a party click changes the query key, so without this `data` is undefined for a beat,
    // the `party_filter` block vanishes, and the control the operator just used — plus D-12's lock on
    // the kind chips — flickers off and back on under their cursor. Serving rows they were already
    // served for a moment longer discloses nothing (same caller, same gates, same governed read);
    // a control that disappears when you press it is a bug they will report.
    //
    // ⛔ **SCOPED TO THE RECORD, and that is not optional** (adversarial review 2026-09-05, B-4).
    // React Query v5 runs a `placeholderData` FUNCTION on ANY key change, and this key is
    // `['crm','record-evidence', listId, entryId, ids]` — so an unscoped `(prev) => prev` also held
    // across `entryId`, i.e. across a ‹ › hop. `CrmRecordTimeline` does not remount on that hop
    // (`useOnceVisible`'s own docstring says so), so deal A's evidence rows, its `hidden_count` and
    // its party chips rendered inside deal B's panel until B answered. Not a governance leak — same
    // caller, rows they were entitled to — but the evidence timeline is a PROVENANCE surface, and
    // briefly attributing one deal's counterparty mail to another is the wrong kind of wrong on it.
    //
    // The rest of this feature is already host-scoped for exactly this reason and says so:
    // `openBodies` and the party selection both reset DURING render on an `entryId` change
    // (`CrmRecordTimeline`, `crmPartyFilter`). The query's own data was the one thing that was not.
    // The `party` axis — the fifth key element, which the hold exists FOR — is deliberately still
    // crossed.
    placeholderData: (prev: CrmRecordEvidenceResponse | undefined, prevQuery) => {
      const key = prevQuery?.queryKey as unknown[] | undefined;
      return key && key[2] === listId && key[3] === entryId ? prev : undefined;
    },
  });
}

/**
 * Pillar F.5 — ONE evidence row's body, fetched on the operator's click.
 *
 * The list payload above carries no body content at all (only `body_available`), so this is the only
 * route to it, and it is deliberately shaped like F.3's `useCrmEmailBody`: `enabled` is the whole
 * point — the caller passes true only after a click, never on render and never on hover. `retry: false`
 * because the endpoint is audited and answers an existence-safe 404 for every refusal; retrying a
 * denial three times would just write three rows into the one log that has to stay readable.
 */
export function useCrmRecordEvidenceBody(
  listId: number | null, entryId: number | null, eventLogId: number | null, enabled: boolean,
) {
  return useQuery<CrmEvidenceBodyResponse>({
    queryKey: ['crm', 'record-evidence-body', listId, entryId, eventLogId],
    queryFn: () => apiFetch<CrmEvidenceBodyResponse>(
      `/api/crm/lists/${listId}/entries/${entryId}/evidence/${eventLogId}/body`),
    enabled: enabled && listId != null && entryId != null && eventLogId != null,
    // F.5 review finding 6 — NO stale window on a body. `staleTime` here meant a deliberate re-open
    // within five minutes served the cached text and wrote NO audit row, so "audited per read" was
    // really "audited per fetch" — and the audit trail is half of why the read moved to the click.
    // A body is <=2,000 chars; the refetch is trivial next to the attribution being correct.
    staleTime: 0,
    retry: false,
  });
}

/**
 * Party-facts WP-6b / lever E2 — **the governed trace behind one fact**, for the "why?" popover.
 *
 * `enabled` is the whole point and is passed `false` until the popover MOUNTS: this is an audited
 * governed read (it writes a `fact_evidence` row and composes the deal's own evidence read behind
 * it), so firing it on hover, on render, or for every chip on the panel would put dozens of audit
 * rows behind one page view and make the trail unreadable. Same discipline as `useCrmEmailBody` and
 * the F.5 body door.
 *
 * `retry: false` because every refusal is an existence-safe 404 — retrying a denial three times
 * writes three rows into the log that has to stay readable.
 */
export function useCrmFactEvidence(assertionId: number | null, enabled: boolean) {
  return useQuery<CrmFactEvidenceResponse>({
    queryKey: ['crm', 'fact-evidence', assertionId],
    queryFn: () => apiFetch<CrmFactEvidenceResponse>(`/api/crm/facts/${assertionId}/evidence`),
    enabled: enabled && assertionId != null,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Lever I — write or withdraw the CALLER'S OWN 25-word note on a fact's lineage.
 *
 * ⚠ There is no `author_kind` on the wire, deliberately: the server decides the kind from the
 * principal, so a client cannot ask to write the model's note (rule 1, made structural rather than
 * validated). The invalidation is the trace query, because `stale` and the note's own text are
 * computed server-side at read time — re-deriving either here would be a second implementation of
 * rule 3.
 */
export function useWriteCrmFactNote(assertionId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ text, withdraw }: { text: string; withdraw?: boolean }) =>
      apiFetch<CrmFactNoteWriteResponse>(`/api/crm/facts/${assertionId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ text, withdraw: !!withdraw }),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm', 'fact-evidence', assertionId] }),
  });
}

/** {field_key → labels} for a list's enum-ish columns — feeds the grid's select editors (M3.3). */
export function useCrmListOptions(listId: number | null) {
  return useQuery<CrmListOptionsResponse>({
    queryKey: ['crm', 'options', listId],
    queryFn: () => apiFetch<CrmListOptionsResponse>(`/api/crm/lists/${listId}/options`),
    enabled: listId != null,
    staleTime: 5 * 60_000, // backend TTL-caches the Affinity fetch too
  });
}

/**
 * The three caches that show one list's record data: the grid page, the v1 record payload (D2b) and
 * the governed `record_review.v2` read (R5-FE). EVERY mutation that changes a record's field values,
 * overlay/push state or lifecycle must invalidate ALL THREE — the record panel reads v2, so a
 * mutation that skipped it left the panel showing stale values until an unrelated review action or a
 * manual refetch refreshed the cache (post-cutover brief §2.4).
 *
 * Exempt: layout-only mutations (`useSetRecordLayout`) — v2 carries fields/lifecycle, never layout.
 */
function _invalidateRecordSurfaces(qc: ReturnType<typeof useQueryClient>, listId: number) {
  qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'record', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'record-v2', listId] });
}

/** ⚑ Refute one evidence row on a record — the CRM-governed twin of the Deals tab's unbind (that
 * endpoint applies neither the D8 audience gate nor the per-mailbox §9 gate). The server flips the
 * row (and every mailbox copy of the same email) to `resolution_status='noise'`, may auto-dismiss a
 * candidate that just lost its last evidence, and re-aggregates the deal.
 *
 * REBIND ("belongs to a different deal") is not offered here: the target picker is Deals-tab-coupled
 * (`/api/deal-cards/search`, ungoverned), so moving evidence between deals stays on that tab.
 *
 * Invalidates the evidence query AND all three record surfaces — a refute can change the deal's
 * proposals and its pending state, not just the timeline. */
export function useRefuteRecordEvidence(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, eventLogId, reason }:
                 { entryId: number; eventLogId: number; reason: string }) =>
      apiFetch<CrmRecordEvidenceRefuteResponse>(
        `/api/crm/lists/${listId}/entries/${entryId}/evidence/${eventLogId}/refute`,
        { method: 'POST', body: JSON.stringify({ reason }) }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'record-evidence', listId] });
      _invalidateRecordSurfaces(qc, listId);
    },
  });
}

/** v1 searches the IOT `deal_pipeline` list only (spec §3.3). Named, not inlined, so the day a second
 *  pipeline arrives the change is here and in the provider — not scattered through the modal. */
export const EVIDENCE_LINK_LIST_ID = 1;
export const EVIDENCE_LINK_PIPELINE_KIND = 'deal' as const;

/** The governed target picker for the manual email-to-record link (spec §7.1) — a sibling of
 *  `useEntryMergeTargets`, and governed the same way: every candidate has passed list access, entry
 *  visibility and (for a proposed record) the full-row audience clause SERVER-side. A blank `q`
 *  returns recent accessible deals, so the picker is useful before the first keystroke.
 *
 *  ⚠ The caller debounces; this hook does not. `q` is in the key, so an undebounced input would be
 *  one governed, audited read per keystroke — the reason every other CRM picker in this file is fed
 *  a 250ms-settled value. */
export function useEvidenceLinkTargets(q: string, enabled = true) {
  return useQuery<CrmEvidenceLinkTargetsResponse>({
    queryKey: ['crm', 'evidence-link-targets', EVIDENCE_LINK_PIPELINE_KIND, EVIDENCE_LINK_LIST_ID, q],
    queryFn: () => apiFetch<CrmEvidenceLinkTargetsResponse>(
      `/api/crm/evidence-link-targets?pipeline_kind=${EVIDENCE_LINK_PIPELINE_KIND}`
      + `&list_id=${EVIDENCE_LINK_LIST_ID}&q=${encodeURIComponent(q)}&limit=20`),
    enabled,
    staleTime: 30_000,
  });
}

/** Link one email — or the current governed snapshot of its thread — to an IOT deal (spec §7.2).
 *
 * The command is ADDITIVE and IDEMPOTENT: an existing link to the same record is a success that
 * reports `already_linked`, and a link to another record is never silently removed. There is NO
 * preflight read in v1 (spec §3.4) — the POST is the first and only request the modal makes.
 *
 * ⚠ INVALIDATION IS BY PREFIX, AND THAT IS THE WHOLE POINT. The source timeline key is
 * `['crm','timeline',kind,id,limit,types,before]` — seven segments, of which three are filter state
 * the mutation cannot know. Invalidating the four-segment prefix catches every filter/page variant
 * the operator may have open; naming the full key would refresh the unfiltered view and leave a
 * filtered one showing pre-link state. The target side reuses `useRefuteRecordEvidence`'s set
 * (`record-evidence` + all three record surfaces) because a link changes the same things a refute
 * does, in the other direction: evidence rows, proposals and pending counts. */
export function useLinkEmailToRecord() {
  const qc = useQueryClient();
  return useMutation<
    CrmLinkEmailToRecordResponse,
    Error,
    {
      messageId: number;
      target: CrmEvidenceLinkTargetRef;
      scope: 'message' | 'thread';
      expectedThreadId: string | null;
      idempotencyKey: string;
      /** The profile the Link was invoked from — only used to invalidate that timeline. */
      sourceKind: CrmEntityKind;
      sourceId: number;
    }
  >({
    mutationFn: (v) => apiFetch<CrmLinkEmailToRecordResponse>(
      `/api/crm/email/${v.messageId}/record-links`,
      {
        method: 'POST',
        body: JSON.stringify({
          target: v.target,
          scope: v.scope,
          expected_thread_id: v.expectedThreadId,
          idempotency_key: v.idempotencyKey,
        }),
      }),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['crm', 'timeline', v.sourceKind, v.sourceId] });
      qc.invalidateQueries({ queryKey: ['crm', 'record-evidence', v.target.list_id] });
      _invalidateRecordSurfaces(qc, v.target.list_id);
    },
  });
}

// `useSyncList` lived here — `POST /api/crm/lists/{id}/sync`, the "Sync now" door that drained a
// list's pending overlay edits to Affinity. Deleted with the route and with `crm/push.py` by the
// Pillar-D write funeral (crm-of-record-plan §5, 2026-08-27). It had been a guaranteed no-op since the
// 2026-08-08 cut — `write_router='local'` for `deal_fields` made the drain return `skipped_routing`,
// so every click returned `{attempted: 0, pushed: 0, failed: 0}` — and its two callers (the grid's
// sync dot, the record panel's per-field Retry) went with it in the same change.

/** Accept the active AI proposal on one field — becomes an operator edit (M3.4). The inline
 *  auto-push to Affinity that used to follow was deleted with the drain by the Pillar-D write
 *  funeral (crm-of-record-plan §5, 2026-08-27); the response no longer carries `pushed`/`push_error`. */
export function useAcceptProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    // expectedProposalId echoes the reviewed proposal so a newer aggregator run → 409 stale (never
    // acts on an unseen value). Sent as body.expected_proposal_id.
    mutationFn: ({ entryId, fieldKey, expectedProposalId }:
                 { entryId: number; fieldKey: string; expectedProposalId?: number }) =>
      apiFetch<{ push_error?: string }>(
        `/api/crm/lists/${listId}/entries/${entryId}/proposals/${encodeURIComponent(fieldKey)}/accept`,
        { method: 'POST', body: JSON.stringify({ expected_proposal_id: expectedProposalId ?? null }) }),
    // keep grid + record panel (v1 + v2) in lockstep (D2b)
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** Dismiss the active AI proposal on one field (a durable learning signal). */
export function useDismissProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, fieldKey, expectedProposalId }:
                 { entryId: number; fieldKey: string; expectedProposalId?: number }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/proposals/${encodeURIComponent(fieldKey)}/dismiss`,
        { method: 'POST', body: JSON.stringify({ expected_proposal_id: expectedProposalId ?? null }) }),
    // keep grid + record panel (v1 + v2) in lockstep (D2b)
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

// ── E2e-3 §4.3.6: the tri-action on PROJECTED (fact-backed) columns ──────────────────────────────
// Reject / Edit / Accept map onto ledger commands (refute / manual supersession / human confirmation),
// NOT onto the proposal or overlay layers — a projected column's truth is the relationship fact, and
// an edit routed through the EAV would write a layer the projection overwrites on the next recompute.
// `expectedRevision` is the revision the panel RENDERED: a 409 means a producer moved the fact since,
// and the caller must refetch rather than decide about a value nobody saw.
export function useProjectedFactAction(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    // `facts` echoes what the panel RENDERED — the assertions and the revisions they were shown at,
    // which is what makes the server's CAS describe the operator's screen.
    //
    // ⚠ Since §1.2 the caller sends exactly ONE member, deliberately. The list shape stays because it
    // is the endpoint's contract and the server still decides everything in it atomically — but the
    // modal edits one fact at a time, so "send every member" is no longer the caller's job. That rule
    // was right for a control that could only mean "this whole value is wrong" and wrong for a role
    // correction, which would otherwise rewrite a sibling fact the operator never touched.
    //
    // `qualifiers` carries a ROLE change on an edit (the entity then stays as it is, so `localId` is
    // omitted), and `reason` is REQUIRED on a reject — the server refuses without it.
    mutationFn: ({ entryId, action, facts, localId, qualifiers, reason }:
                 { entryId: number; action: 'reject' | 'accept' | 'edit';
                   facts: { assertion_id: number; revision: number }[]; localId?: number;
                   qualifiers?: Record<string, string>; reason?: string }) =>
      apiFetch<{ entries_reprojected: number; reprojection_incomplete: boolean }>(
        `/api/crm/lists/${listId}/entries/${entryId}/facts/${action}`,
        { method: 'POST', body: JSON.stringify({
          facts: facts.map((f) => ({ assertion_id: f.assertion_id, expected_revision: f.revision })),
          local_id: localId ?? null,
          qualifiers: qualifiers ?? null,
          reason: reason ?? null }) }),
    onSettled: () => {
      // The command reprojects server-side, so BOTH surfaces have new values to read — the grid cell
      // and the panel's chips come from the same recomputed blob.
      _invalidateRecordSurfaces(qc, listId);
    },
  });
}

// ── notes-provenance review queue (the NEW lifecycle resolver path — spec §10) ───────────────────
// These hit the review-* endpoints (by proposal_id) that route through proposal_lifecycle._resolve_proposal
// — publishing accepted values into the settled, non-pushable crm_field_resolved_value layer (never
// Affinity) + recording learning feedback. DISTINCT from useAcceptProposal/useDismissProposal above,
// which are the legacy grid overlay+push path. All invalidate the same three record surfaces
// (`_invalidateRecordSurfaces`) as every other value-writing mutation.
// A 409 (a newer aggregator run superseded the reviewed proposal) surfaces as ApiError.status===409 so
// the caller refetches — the stale-card contract; there is no passive autosave.

/** Build a JSON request body, folding in the optional R2b record-level CAS token (record_revision +
 * proposal_set_hash) when the caller supplies it. Snake_case matches the backend `_ReviewCasBody`, so
 * the token spreads straight in; omit `cas` → no record-level staleness check (per-proposal CAS only). */
function casBody(base: Record<string, unknown>, cas?: RecordReviewCas): string {
  return JSON.stringify(cas ? { ...base, ...cas } : base);
}

/** Accept a pending AI proposal via the lifecycle resolver (publishes the AI value to the settled layer). */
export function useReviewAcceptProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, proposalId, cas }:
                 { entryId: number; proposalId: number; cas?: RecordReviewCas }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/proposals/${proposalId}/review-accept`,
        { method: 'POST', body: casBody({}, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** Edit-and-accept: publish the operator's edited value (explicit confirm, never autosave). */
export function useReviewEditAcceptProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, proposalId, value, cas }:
                 { entryId: number; proposalId: number; value: unknown; cas?: RecordReviewCas }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/proposals/${proposalId}/review-edit-accept`,
        { method: 'POST', body: casBody({ value }, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** Reject a pending AI proposal (records feedback; publishes nothing). reason ∈ FIELD_DISMISS_REASONS. */
export function useReviewRejectProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, proposalId, reason, note, cas }:
                 { entryId: number; proposalId: number; reason?: string; note?: string; cas?: RecordReviewCas }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/proposals/${proposalId}/review-reject`,
        { method: 'POST', body: casBody({ reason: reason ?? null, note: note ?? null }, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** R5b-1: Restore (un-reject) a REJECTED field proposal — the server clones it into a fresh PENDING
 * proposal (value + evidence lineage) so it re-enters review. Works on committed AND proposed (draft)
 * rows. 404 = not a rejected proposal on this entry / not visible; 409 = a newer run superseded the
 * field since the reject, or (with the echoed `cas`) the record's member set changed (R2b). */
export function useReviewRestoreProposal(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, proposalId, cas }:
                 { entryId: number; proposalId: number; cas?: RecordReviewCas }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/proposals/${proposalId}/review-restore`,
        { method: 'POST', body: casBody({}, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** R5b-2: a NON-publishing draft-override edit of one field on a PROPOSED (draft) row — kept as an
 * overlay (never the settled layer, never Affinity) and materialized only at the record-level Accept
 * (R5c). Body {value}. 409 = the row is committed (use the M3.2 edit path) or the R2b CAS tripped;
 * 400 = unknown field; 404 = row/field not visible. */
export function useDraftEditField(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, fieldKey, value, cas }:
                 { entryId: number; fieldKey: string; value: unknown; cas?: RecordReviewCas }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}/fields/${encodeURIComponent(fieldKey)}/draft-edit`,
        { method: 'POST', body: casBody({ value }, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/** Bulk-accept every pending proposal on a COMMITTED entry (each its own CAS). */
export function useReviewAcceptAll(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, cas }: { entryId: number; cas?: RecordReviewCas }) =>
      apiFetch<{ accepted: number; skipped: number; total: number }>(
        `/api/crm/lists/${listId}/entries/${entryId}/proposals/accept-all`,
        { method: 'POST', body: casBody({}, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/**
 * "Dismiss AI" (round-2 §1) — the disposal twin of accept-all on a COMMITTED entry.
 *
 * The dismissals are value-sticky server-side: an aggregator run that re-proposes the same values does
 * not bring them back. The RECORD is untouched — this disposes of suggestions, not of the deal.
 */
export function useReviewDismissAllAi(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, cas }: { entryId: number; cas?: RecordReviewCas }) =>
      apiFetch<{ dismissed: number; skipped: number; total: number }>(
        `/api/crm/lists/${listId}/entries/${entryId}/proposals/dismiss-all-ai`,
        { method: 'POST', body: casBody({}, cas) }),
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

/**
 * §3/P4's fourth operation — a human asserts a relationship the machine never proposed.
 *
 * Its own hook because an add has no chip: no `facts` list and no CAS, since there is no existing
 * revision to be stale about. The server keys idempotency on the value, so a double-submit writes once.
 *
 * The refusal worth handling in the UI is 400 `correction_refused` when the value was previously
 * REJECTED by a person — the ledger drops that write silently, and the endpoint turns it into an
 * explicit message rather than a success that wrote nothing.
 */
export function useAddProjectedFact(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, relationshipType, fromKind, localId, qualifiers, reason }:
                 { entryId: number; relationshipType: string;
                   fromKind: 'person' | 'organization'; localId: number;
                   qualifiers?: Record<string, string>; reason?: string }) =>
      apiFetch<{ entries_reprojected: number; reprojection_incomplete: boolean }>(
        `/api/crm/lists/${listId}/entries/${entryId}/facts/add`,
        { method: 'POST', body: JSON.stringify({
          relationship_type: relationshipType, from_kind: fromKind,
          local_id: localId, qualifiers: qualifiers ?? null, reason: reason ?? null }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
    },
  });
}

/**
 * Spec §1.2: change what KIND of relationship ONE fact records (the Westwood case).
 *
 * Its own endpoint rather than another `action` on the tri-action route, because it is not an edit:
 * `relationship_type` is an input to `fact_key`, so the server performs a refute of the old fact plus a
 * new predecessor-linked one. `expectedRevision` is REQUIRED — there is a live fact on screen to be
 * stale about, and a retype landing on a revision the operator never saw retracts something they did
 * not choose.
 */
export function useRetypeProjectedFact(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, assertionId, expectedRevision, relationshipType, qualifiers }:
                 { entryId: number; assertionId: number; expectedRevision: number;
                   relationshipType: string; qualifiers?: Record<string, string> }) =>
      apiFetch<{ entries_reprojected: number; reprojection_incomplete: boolean }>(
        `/api/crm/lists/${listId}/entries/${entryId}/facts/retype`,
        { method: 'POST', body: JSON.stringify({
          assertion_id: assertionId, expected_revision: expectedRevision,
          relationship_type: relationshipType, qualifiers: qualifiers ?? null }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
    },
  });
}

/**
 * Spec §1.3 — read a sentence into a DRAFT relationship fact. **This endpoint WRITES NOTHING.**
 *
 * ⚠ A mutation hook only because it is a POST with a body; it invalidates NOTHING, because nothing
 * changed. The draft it returns fills the modal's slot controls, the operator edits or accepts, and the
 * EXISTING `facts/add` performs the write — so an invented qualifier is refused by the gates already on
 * that path rather than by the model behaving well.
 *
 * Stateless, two turns maximum: pass `priorProposal` + `answer` for the second turn and nothing more.
 */
export function useInterpretFact(listId: number) {
  return useMutation({
    mutationFn: ({ entryId, fieldKey, text, priorProposal, answer }:
                 { entryId: number; fieldKey: string; text: string;
                   priorProposal?: unknown; answer?: string }) =>
      apiFetch<CrmFactInterpretResponse>(
        `/api/crm/lists/${listId}/entries/${entryId}/facts/interpret`,
        { method: 'POST', body: JSON.stringify({
          field_key: fieldKey, text,
          prior_proposal: priorProposal ?? null, answer: answer ?? null }) }),
  });
}

// ── D8: whole-record proposals (proposed candidate rows in the deal-pipeline list) ──────────────
/** Accept a PROPOSED candidate record → promote it to committed. Reuses the Deals-tab `log_deal`
 * (creates the Affinity opp + promotes the candidate); the entry flips in place so its `rec` deep-link
 * stays valid. Returns {status, opp_id}; the route 502s on a promote failure, 404s if the entry isn't
 * a visible proposed record. Invalidates the grid + record + lists catalog (the count/badge move). */
export function useAcceptRecord(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    // R5c: an optional R2b CAS echo (from adaptRecordReviewV2().reviewCas) engages the record-level
    // stale-set guard (409 if a newer proposal landed since render). Omitted → no body, exactly the
    // legacy request (accept-record's body is `_ReviewCasBody | None`).
    // ⚠ The response type carries `materialized` — NOT decoration. Its `refused` list is the only
    // report that a fresh-authored draft field (e.g. a Deal Team whose member left the roster since the
    // draft edit) was NOT published, and the server clears the draft overlays either way. Typing it away
    // is how the operator's typed value disappears with the panel.
    mutationFn: ({ entryId, cas }: { entryId: number; cas?: RecordReviewCas }) =>
      apiFetch<CrmAcceptRecordResponse>(
        `/api/crm/lists/${listId}/entries/${entryId}/accept-record`,
        cas ? { method: 'POST', body: JSON.stringify(cas) } : { method: 'POST' }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] }); // the committed/proposed counts move
    },
  });
}

/** Dismiss a PROPOSED candidate record → suppress it (dismisses the underlying candidate via the
 * Deals-tab deal-level dismiss + retires the proposed entry). Body {reason, note?}; the route 400s on
 * an invalid reason, 404s if the entry isn't a visible proposed record. */
export function useDismissRecord(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, reason, note }: { entryId: number; reason: string; note?: string }) =>
      apiFetch<{ status: string }>(
        `/api/crm/lists/${listId}/entries/${entryId}/dismiss-record`,
        { method: 'POST', body: JSON.stringify({ reason, note: note ?? null }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
    },
  });
}

/** Pass a PROPOSED candidate record → promote-to-Passed shortcut. Reuses the Deals-tab `log_pass`
 * (creates the Affinity opp at terminal stage 'Passed' + the Lost/Passed category + note); the entry
 * flips proposed→committed in place. Body {category, note?}; the route 400s on an invalid category,
 * 404s if not a visible proposed record, 502s on an Affinity write failure. */
export function usePassRecord(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    // Same `materialized` contract as Accept — Pass materializes the curated review too.
    // …and, since Slice 0, the same OPTIONAL R2b CAS echo: Pass is TERMINAL, so a stale panel is the
    // write that most needs the 409. The server-side CAS existed all along; this route sent no token,
    // which is what made it dead code. `cas` omitted → no record-level staleness check (unchanged).
    mutationFn: ({ entryId, category, note, cas }:
                 { entryId: number; category: string; note?: string; cas?: RecordReviewCas }) =>
      apiFetch<CrmAcceptRecordResponse>(
        `/api/crm/lists/${listId}/entries/${entryId}/pass-record`,
        { method: 'POST', body: casBody({ category, note: note ?? null }, cas) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
    },
  });
}

/** Rename a PROPOSED (draft) candidate record. A draft's name is candidate data, not a cell — the
 * D8 rule refuses cell edits until Accept — so this hits the dedicated rename-draft route, which
 * writes the candidate row and refolds the preview projection. 400 on an empty name; 409 when the
 * record is already committed (its name is then an ordinary field edit). */
export function useRenameDraftRecord(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, name }: { entryId: number; name: string }) =>
      apiFetch<{ status: string; name?: string }>(
        `/api/crm/lists/${listId}/entries/${entryId}/rename-draft`,
        { method: 'POST', body: JSON.stringify({ name }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
    },
  });
}

/** Merge a PROPOSED candidate record INTO an existing deal (class-B over-create remedy). Reuses the
 * Deals-tab `merge_deal_into` (source = this candidate → operator-picked target), then retires the
 * proposed entry. Body {target_deal_kind, target_deal_key}; 404s if the entry/target isn't found,
 * 502s on a failed/partial merge (the entry stays proposable for retry). */
export function useMergeRecord(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, targetDealKind, targetDealKey }:
      { entryId: number; targetDealKind: string; targetDealKey: string }) =>
      apiFetch<{ status: string }>(
        `/api/crm/lists/${listId}/entries/${entryId}/merge-record`,
        { method: 'POST', body: JSON.stringify({ target_deal_kind: targetDealKind, target_deal_key: targetDealKey }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
    },
  });
}

// ── The NATIVE deal merge (entry → entry) ───────────────────────────────────────────────────────
//
// `useMergeRecord` above is the PRE-CUT merge: its target is a candidate uuid or a frozen Affinity opp
// id, and its picker (`/api/deal-cards/search`) cannot offer a committed native deal at all. These
// three reach E2d-3's `merge_list_entries` instead, whose target IS a native list entry — which is what
// makes folding two real IOT rows together possible.

/** The governed Merge picker: committed, visible, non-merged-away entries of this list.
 *
 * Unlike `/api/deal-cards/search` every candidate here passes the CRM's own gate, which is the
 * precondition for letting a governed surface act on a target at all. Enabled only with an entry —
 * the picker is per-source (it filters the source out). */
export function useEntryMergeTargets(listId: number | null, entryId: number | null, q: string) {
  return useQuery<{ targets: CrmDealMergeTarget[] }>({
    queryKey: ['crm', 'merge-targets', listId, entryId, q],
    queryFn: () => apiFetch<{ targets: CrmDealMergeTarget[] }>(
      `/api/crm/lists/${listId}/entries/${entryId}/merge-targets?q=${encodeURIComponent(q)}&limit=20`),
    enabled: listId != null && entryId != null,
    staleTime: 30_000,
  });
}

/** Non-mutating preview of an entry→entry merge: rows that would move, fields that disagree, and the
 * audience delta. A POST because the survivor travels in the body, not because it writes. */
export function useEntryMergePreview(listId: number) {
  return useMutation({
    mutationFn: ({ entryId, survivorEntryId }: { entryId: number; survivorEntryId: number }) =>
      apiFetch<CrmDealMergePreview>(
        `/api/crm/lists/${listId}/entries/${entryId}/merge-preview`,
        { method: 'POST', body: JSON.stringify({ survivor_entry_id: survivorEntryId }) }),
  });
}

/** Commit the entry→entry merge. 409 carries the undecided conflicts (each with its `choice_key`), so
 * a refusal is a form to complete rather than a dead end. Invalidates the grid AND the list catalog:
 * the source row leaves the list and the survivor's counts change. */
export function useMergeIntoEntry(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, survivorEntryId, conflictChoices }:
      { entryId: number; survivorEntryId: number; conflictChoices?: Record<string, string> }) =>
      apiFetch<{ status: string; manifest?: Record<string, unknown> }>(
        `/api/crm/lists/${listId}/entries/${entryId}/merge-into-entry`,
        { method: 'POST', body: JSON.stringify({
          survivor_entry_id: survivorEntryId, conflict_choices: conflictChoices ?? {},
        }) }),
    onSettled: () => {
      _invalidateRecordSurfaces(qc, listId);
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
      qc.invalidateQueries({ queryKey: ['crm', 'merge-targets', listId] });
    },
  });
}

/** Saved grid views for a list (the caller's own + shared). */
export function useCrmListViews(listId: number | null) {
  return useQuery<CrmViewsResponse>({
    queryKey: ['crm', 'views', listId],
    queryFn: () => apiFetch<CrmViewsResponse>(`/api/crm/lists/${listId}/views`),
    enabled: listId != null,
    staleTime: 60_000,
  });
}

export function useCreateView(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; config: Record<string, unknown>; shared: boolean; is_default?: boolean }) =>
      apiFetch<CrmView>(`/api/crm/lists/${listId}/views`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'views', listId] }),
  });
}

export function useUpdateView(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, ...body }: { viewId: number; name?: string; config?: Record<string, unknown>; shared?: boolean; is_default?: boolean }) =>
      apiFetch<CrmView>(`/api/crm/views/${viewId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'views', listId] }),
  });
}

export function useDeleteView(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: number) =>
      apiFetch<{ deleted: boolean }>(`/api/crm/views/${viewId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'views', listId] }),
  });
}

/** ADMIN-only (server gate: `require_admin`): point the LIST's team default view at a shared view, or
 * clear it with `null`. Invalidates the VIEWS query and nothing else — a team default changes which
 * saved layout opens, never a row, a column or a record surface. */
export function useSetTeamDefaultView(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: number | null) =>
      apiFetch<{ list_id: number; team_default_view_id: number | null }>(
        `/api/crm/admin/lists/${listId}/team-default-view`,
        { method: 'PUT', body: JSON.stringify({ view_id: viewId }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'views', listId] }),
  });
}

// ── Item D (D2c): per-list record-panel layout ────────────────────────────────────────────────
/** Read the per-list record layout on its OWN list-keyed query, independent of any single record.
 * The layout also arrives bundled in each `useCrmRecord` payload, but that bundling is exactly what
 * causes the panel's open-time field "jitter": the first paint (from the grid-row snapshot) has no
 * layout, so fields render in flat grid-column order, then the record hydrates with the custom layout
 * and they REORDER. Prefetching this at grid-load (CrmListGrid) warms the cache so the panel paints in
 * the FINAL order from frame one — values fill in on hydration, no reorder. Long staleTime: the layout
 * only changes when an admin edits it (which invalidates this key). */
export function useCrmRecordLayout(listId: number | null) {
  return useQuery<CrmRecordLayout>({
    queryKey: ['crm', 'record-layout', listId],
    queryFn: () => apiFetch<CrmRecordLayout>(`/api/crm/lists/${listId}/record-layout`),
    enabled: listId != null,
    staleTime: 5 * 60_000,
  });
}

/** Set the per-list record layout (ADMIN only, server-enforced). Invalidates the record payloads +
 * the standalone layout query so every open panel on the list re-hydrates its sections. */
export function useSetRecordLayout(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sections, hiddenFieldKeys }: {
      sections: CrmRecordLayoutSection[];
      // §15 item 6 — an EXCLUSION list. Always sent (never omitted), so unticking the last hidden
      // field actually clears it rather than leaving the previous set stored.
      hiddenFieldKeys: string[];
    }) =>
      apiFetch<CrmRecordLayout>(`/api/crm/lists/${listId}/record-layout`, {
        method: 'PUT',
        body: JSON.stringify({ sections, hidden_field_keys: hiddenFieldKeys }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'record', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'record-layout', listId] });
    },
  });
}

// ── native lists (M3.1): create + locally edit ─────────────────────────────────────────────
/** The L1 creation-wizard template catalog (static; cached long). */
export function useListTemplates() {
  return useQuery<CrmListTemplatesResponse>({
    queryKey: ['crm', 'list-templates'],
    queryFn: () => apiFetch<CrmListTemplatesResponse>('/api/crm/list-templates'),
    staleTime: 30 * 60_000,
  });
}

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; permission_class?: string;
      template_key?: string | null; entry_subject_kind?: string | null;
    }) => apiFetch<CrmListSummary>('/api/crm/lists', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'lists'] }),
  });
}

/** Rename a native list (owner-only; Slice 8). Invalidates the catalog. */
export function useUpdateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, name }: { listId: number; name: string }) =>
      apiFetch<{ id: number; name: string }>(`/api/crm/lists/${listId}`,
        { method: 'PATCH', body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'lists'] }),
  });
}

/** Enable/disable the opt-in interaction-derived columns on a native list (config_json.derived).
 * Invalidates the grid so the newly-enabled column is computed + emitted. */
export function useSetListDerived(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (derived: string[]) =>
      apiFetch<{ derived: string[] }>(`/api/crm/lists/${listId}/derived`,
        { method: 'PATCH', body: JSON.stringify({ derived }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] }),
  });
}

// ── list sharing v1 ───────────────────────────────────────────────────────────────────────────
// Spec: docs/specs/strategic-architecture/list-sharing-spec.md. One access level, FULL: a share
// makes the holder functionally the owner. Every mutation here invalidates BOTH the grid (its
// payload carries can_share/shared_count) and the catalog (a granted list appears / a left list
// disappears), because a share changes who can see the list — not just what this dialog shows.
const _invalidateSharing = (qc: ReturnType<typeof useQueryClient>, listId: number) => {
  qc.invalidateQueries({ queryKey: ['crm', 'list-shares', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
};

/**
 * Who a list may be SHARED WITH — the Share typeahead's source (the ACTIVE Ember roster).
 *
 * ⛔ NOT `useCrmLookup`. v1's dialog reused the general directory typeahead, so Share offered every
 * counterparty in the CRM (BMO, Jefferies, IADB) — the owner-reported defect behind the 2026-08-27
 * ruling: *internal users with logins only*. The backend refuses a non-login grantee regardless
 * (`not_internal_user`); this is what stops an operator being led there.
 *
 * Fetched ONCE per open and filtered client-side: the roster is tens of rows, so a debounce and a
 * request per keystroke would buy nothing. Long `staleTime` for the same reason `useTeamRoster` has
 * one — roster edits are rare — while `within_ceiling` is list-scoped, hence the listId in the key.
 */
export function useShareCandidates(listId: number | null, enabled = true) {
  return useQuery<CrmShareCandidatesResponse>({
    queryKey: ['crm', 'share-candidates', listId],
    queryFn: () => apiFetch<CrmShareCandidatesResponse>(
      `/api/crm/lists/${listId}/share-candidates`),
    enabled: listId != null && enabled,
    staleTime: 5 * 60_000,
  });
}

/** Who has access to a list (owner + active shares). Enabled only while the dialog is open. */
export function useListShares(listId: number | null, enabled = true) {
  return useQuery<CrmListSharesResponse>({
    queryKey: ['crm', 'list-shares', listId],
    queryFn: () => apiFetch<CrmListSharesResponse>(`/api/crm/lists/${listId}/shares`),
    enabled: listId != null && enabled,
    staleTime: 30_000,
  });
}

/**
 * Share a list with one or more addresses. Per-address outcomes come back in `results`.
 *
 * ⚠ **The response's access table is written INTO the query cache, never rendered from the mutation.**
 * The dialog's single source of truth is `useListShares`; a component that preferred
 * `grant.data ?? shares.data` froze the table for the rest of its lifetime, because React Query
 * keeps mutation data after settle and the revoke is a different mutation (adv-review [0]/[2]/[3]/[10]
 * — a revoked person stayed listed with a live ✕ that then 404'd). `setQueryData` makes the fresh
 * table the query's value immediately (no refetch round-trip), and the invalidation still fires so
 * the grid/catalog and any other observer catch up.
 */
export function useGrantListShares(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emails: string[]) =>
      apiFetch<CrmShareGrantResponse>(`/api/crm/lists/${listId}/shares`,
        { method: 'POST', body: JSON.stringify({ emails }) }),
    onSuccess: (out) => {
      const { results: _results, counts: _counts, ...table } = out;
      qc.setQueryData(['crm', 'list-shares', listId], table);
      _invalidateSharing(qc, listId);
    },
  });
}

/**
 * Withdraw one share — or leave the list yourself (the backend accepts the holder's own door).
 *
 * The response carries the refreshed `shares` array precisely so the caller need not refetch, so it
 * is written into the same cache entry the dialog renders from. On a SELF revoke `shares` is null
 * (the caller can no longer read the list): nothing is written, and the catalog invalidation is what
 * moves them out.
 */
export function useRevokeListShare(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: number) =>
      apiFetch<CrmShareRevokeResponse>(`/api/crm/lists/${listId}/shares/${shareId}`,
        { method: 'DELETE' }),
    onSuccess: (out) => {
      if (out.shares) {
        qc.setQueryData<CrmListSharesResponse>(['crm', 'list-shares', listId],
          (prev) => (prev ? { ...prev, shares: out.shares as CrmListShare[] } : prev));
      }
      _invalidateSharing(qc, listId);
    },
  });
}

/** Soft-delete a whole native list (owner-only; backend 404s on non-owner / non-native). */
export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: number) =>
      apiFetch<{ deleted: boolean }>(`/api/crm/lists/${listId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'lists'] }),
  });
}

/** Soft-delete one row from a native list. Invalidates the grid + the catalog (entry count). */
export function useDeleteEntry(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      apiFetch<{ deleted: boolean }>(`/api/crm/lists/${listId}/entries/${entryId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'lists'] });
    },
  });
}

/** Add a directory record as a membership row on a person/org list (L1). */
export function useAddMember(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subject_kind: string; subject_id: number }) =>
      apiFetch<CrmAddMemberResult>(`/api/crm/lists/${listId}/members`,
        { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] }),
  });
}

/** L2 directory typeahead (POST /crm/lookup) for the ref-cell chip editor. Keyed on kind + the
 * (debounced) query; gated on >=2 chars. Directory-wide (not list-scoped). */
export function useCrmLookup(kind: 'person' | 'organization', q: string) {
  const query = q.trim();
  return useQuery<CrmLookupResponse>({
    queryKey: ['crm', 'lookup', kind, query],
    queryFn: () => apiFetch<CrmLookupResponse>('/api/crm/lookup',
      { method: 'POST', body: JSON.stringify({ kind, q: query }) }),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

/**
 * Manual ORGANISATION creation (identity notes §3) — the one operation the CRM had no way to perform,
 * which is why a company that has never emailed us could not be represented at all.
 *
 * ONE mutation for BOTH steps, deliberately. The endpoint is search-first: omit `confirm_name` and it
 * returns the near-matches and writes nothing; send `confirm_name` equal to `name` and it creates. A
 * second hook for the search would let the two drift apart, and the whole point is that the operator
 * sees the near-matches the CREATE would have declined — not a different list from a different query.
 *
 * On a successful create the directory typeahead is invalidated so the new company is immediately
 * pickable; the caller selects it directly, so nobody has to search again (which is the trap where an
 * operator picks a DIFFERENT company under a heading naming this one).
 */
export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; primary_domain?: string | null; alt_domains?: string[];
      evidence_note?: string | null; confirm_name?: string | null;
    }) => apiFetch<CrmOrgCreateResponse>('/api/crm/admin/org-create',
      { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (out) => {
      if (out.created) qc.invalidateQueries({ queryKey: ['crm', 'lookup'] });
    },
  });
}

/** Typeahead lookup for the member-add flow. Query keyed on the (debounced) text; gated on >=2 chars. */
export function useMemberLookup(listId: number, q: string) {
  const query = q.trim();
  return useQuery<CrmMemberLookupResponse>({
    queryKey: ['crm', 'member-lookup', listId, query],
    queryFn: () => apiFetch<CrmMemberLookupResponse>(
      `/api/crm/lists/${listId}/member-lookup?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

export function useAddField(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    // M2: options (optional) seeds an enum-family column's picklist at creation.
    mutationFn: (body: { field_key: string; label: string; field_type: string; options?: string[] }) =>
      apiFetch(`/api/crm/lists/${listId}/fields`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      // ALSO refresh the options cache — a column created WITH options must make its picklist
      // usable immediately (else the enum degrades to a free-text editor / ranked_enum is
      // non-editable until the 5-min staleTime, AND the Columns-panel editor prefills empty so a
      // Save would clear the just-created options). Mirror useSetFieldOptions.
      qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
    },
  });
}

/** M2: set an enum-family column's option picklist (order = rank for ranked_enum). Invalidates the
 * grid + the options cache so the select editor picks up the new labels. */
export function useSetFieldOptions(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, options }: { fieldKey: string; options: string[] }) =>
      apiFetch(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/options`,
        { method: 'PATCH', body: JSON.stringify({ options }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
    },
  });
}

// Slice 11: the structured-options editor mutations. All invalidate the grid + options + views.
function _invalidateFieldConfig(qc: ReturnType<typeof useQueryClient>, listId: number) {
  qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
  qc.invalidateQueries({ queryKey: ['crm', 'views', listId] });
}

export interface CrmOptionGroup { label: string; options: { value: string; color?: string }[] }

/** Slice 11: set the STRUCTURED option config (grouped + colored) on a native enum-family column. */
export function useSetFieldConfig(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, groups }: { fieldKey: string; groups: CrmOptionGroup[] }) =>
      apiFetch(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/config`,
        { method: 'PATCH', body: JSON.stringify({ groups }) }),
    onSuccess: () => _invalidateFieldConfig(qc, listId),
  });
}

/** Slice 11: rename an option (rewrites stored cells too). */
export function useRenameOption(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, from, to }: { fieldKey: string; from: string; to: string }) =>
      apiFetch(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/options/rename`,
        { method: 'POST', body: JSON.stringify({ from_value: from, to_value: to }) }),
    onSuccess: () => _invalidateFieldConfig(qc, listId),
  });
}

/** Slice 11: delete an option (409 `{conflict:'in_use', count}` when still used). */
export function useDeleteOption(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, value }: { fieldKey: string; value: string }) =>
      apiFetch(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/options/delete`,
        { method: 'POST', body: JSON.stringify({ value }) }),
    onSuccess: () => _invalidateFieldConfig(qc, listId),
  });
}

/** Slice 6: rename a native-list column's label. Invalidates grid + options + views (a view may
 * pin/sort the field, and the label shows in the columns panel). */
export function useUpdateField(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, label }: { fieldKey: string; label: string }) =>
      apiFetch(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}`,
        { method: 'PATCH', body: JSON.stringify({ label }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'views', listId] });
    },
  });
}

/** Slice 7: retype a native-list column along the safe-conversion matrix. A multi_enum→enum that
 * would drop data rejects 409 `{conflict:'multi_values', count}` (surfaced by the caller). */
export function useConvertFieldType(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldKey, fieldType }: { fieldKey: string; fieldType: string }) =>
      apiFetch<{ field_key: string; field_type: string }>(
        `/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/type`,
        { method: 'PATCH', body: JSON.stringify({ field_type: fieldType }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'views', listId] });
    },
  });
}

/** Slice 6: soft-delete a native-list column (recoverable; historical values kept). */
export function useDeleteField(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fieldKey: string) =>
      apiFetch<{ deleted: boolean }>(`/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}`,
        { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'options', listId] });
      qc.invalidateQueries({ queryKey: ['crm', 'views', listId] });
    },
  });
}

/** Slice 6: how many entries hold a value for a field — the delete-confirmation blast radius.
 * Read on demand (the delete flow imperatively fetches it), so this is a thin fetch helper. */
export async function fetchFieldUsage(listId: number, fieldKey: string): Promise<{ value_count: number }> {
  return apiFetch<{ field_key: string; value_count: number }>(
    `/api/crm/lists/${listId}/fields/${encodeURIComponent(fieldKey)}/usage`);
}

export function useAddEntry(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/api/crm/lists/${listId}/entries`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'grid', listId] }),
  });
}

export function useSetFieldValue(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, field_key, value }: { entryId: number; field_key: string; value: unknown }) =>
      apiFetch(`/api/crm/lists/${listId}/entries/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ field_key, value }),
      }),
    // refetch on BOTH success and error: success syncs the server-coerced value (e.g. "250" → 250),
    // and error ROLLS BACK AG-Grid's optimistic in-place edit (else a failed write shows as saved
    // then silently reverts on the next reload). getRowId keeps row identity stable across refetch.
    // Also refresh BOTH record payloads — the v1 read (D2b) and the governed record_review.v2 read the
    // panel actually renders from (R5-FE) — so its field state matches the grid. Missing v2 here left
    // the panel stale after a plain edit until an unrelated review action refetched it (brief §2.4).
    onSettled: () => _invalidateRecordSurfaces(qc, listId),
  });
}

export function useCrmProfile(kind: CrmEntityKind, id: number | null) {
  return useQuery<CrmEntityProfile>({
    queryKey: ['crm', 'profile', kind, id],
    queryFn: () => apiFetch<CrmEntityProfile>(`/api/crm/${kindPath(kind)}/${id}`),
    enabled: id != null,
    staleTime: 60_000,
  });
}

/** ⋯ → Edit name: manual name override (+ manual_locked so ingest/ER won't clobber it). */
export function useRenameEntity(kind: CrmEntityKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (primary_name: string) =>
      apiFetch<{ id: number; primary_name: string }>(`/api/crm/${kindPath(kind)}/${id}`,
        { method: 'PATCH', body: JSON.stringify({ primary_name }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'profile', kind, id] });
      // the rename appended a tier-2 user observation → refresh the ✦/history affordance too
      qc.invalidateQueries({ queryKey: ['crm', 'attr-history', kind, id] });
    },
  });
}

/** Inline person-title edit (integration F7) — records a source='user' tier-2 title observation. Refreshes
 *  the ✦/history + the employment card (the title fallback source). */
export function useEditPersonTitle(personId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) =>
      apiFetch<{ id: number; title: string }>(`/api/crm/person/${personId}/title`,
        { method: 'PATCH', body: JSON.stringify({ title }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'attr-history', 'person', personId] });
      qc.invalidateQueries({ queryKey: ['crm', 'employment', personId] });
    },
  });
}

/**
 * ⋯ → Add role: record BY HAND that a person holds a role at an organization.
 *
 * The CRM derives employment from the domain someone mails FROM, which cannot see a board seat — a
 * non-executive director writes from his own address, so nothing links him to the company he chairs.
 * This is the only writer of that link other than entity resolution.
 *
 * Invalidates the person's employment card (where the new row renders), their profile, and EVERY
 * org-people cache: the affiliation is one edge read from both ends, so the company's People card is
 * stale the moment this succeeds.
 */
export function useAddPersonEmployment(personId: number) {
  const qc = useQueryClient();
  return useMutation<CrmAddEmploymentResult, Error,
    { org_id: number; title: string; role?: string }>({
    mutationFn: (body) =>
      apiFetch<CrmAddEmploymentResult>(`/api/crm/person/${personId}/employment`,
        { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'employment', personId] });
      qc.invalidateQueries({ queryKey: ['crm', 'profile', 'person', personId] });
      qc.invalidateQueries({ queryKey: ['crm', 'org-people'] });
    },
  });
}

/** CUT-8: the append-only version history for ONE list cell. Lazy — `enabled` stays false until the
 *  operator opens the affordance, so a panel render never fans out one request per field. Entry-
 *  visibility gated + read-audited server-side. */
export function useCrmFieldHistory(listId: number | null, entryId: number | null,
                                   fieldKey: string | null, enabled = false) {
  return useQuery<CrmFieldHistoryResponse>({
    queryKey: ['crm', 'field-history', listId, entryId, fieldKey],
    queryFn: () => apiFetch<CrmFieldHistoryResponse>(
      `/api/crm/lists/${listId}/entries/${entryId}/fields/${encodeURIComponent(fieldKey!)}/history`),
    enabled: enabled && listId != null && entryId != null && !!fieldKey,
    staleTime: 60_000,
  });
}

/** Ledger-backed identity-attribute history (name/title/seniority/linkedin/org_types) — powers the
 *  profile ✦/history affordance AND the inline render of the enrichment-written T1 facts. Governance-
 *  scoped + audited server-side (same gate as the profile read). */
export function useEntityAttributeHistory(kind: CrmEntityKind, id: number | null, enabled = true) {
  return useQuery<CrmAttrHistoryResponse>({
    queryKey: ['crm', 'attr-history', kind, id],
    queryFn: () => apiFetch<CrmAttrHistoryResponse>(`/api/crm/${kindPath(kind)}/${id}/attribute-history`),
    enabled: enabled && id != null,
    staleTime: 5 * 60_000,
  });
}

/** D-2 phase 1: reject an enrichment-sourced field value (title/division/seniority/linkedin). The
 * backend reverts the enrichment observations carrying the value, records the correction (the E-15
 * learning row), and never re-lands an identity-equal value. Invalidates the attr-history + profile. */
export function useRejectCrmField(personId: number) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { key: string; reason?: string; note?: string }>({
    mutationFn: ({ key, reason, note }) =>
      apiFetch(`/api/crm/person/${personId}/fields/${key}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason ?? null, note: note ?? null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'attr-history', 'person', personId] });
      qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
}

/** D-2 phase 2 (F7 generalized): inline edit for title/division/seniority/linkedin. Records a tier-2
 * user observation (sticky over ordinary enrichment); when it supersedes an enrichment value the backend
 * also records an edit_accept correction row for the E-15 readout. */
export function useEditCrmField(personId: number) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { key: string; value: string }>({
    mutationFn: ({ key, value }) =>
      apiFetch(`/api/crm/person/${personId}/fields/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'attr-history', 'person', personId] });
      qc.invalidateQueries({ queryKey: ['crm', 'employment', personId] });
      qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
}

/** ⋯ → Set as list: retract a fabricated person that is really a shared/functional mailbox. Broadly
 * invalidates ['crm'] — the retract moves entitlement + re-parks the address across timelines/people. */
export function useReclassifyShared(personId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ email: string }>(`/api/crm/person/${personId}/reclassify-shared`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });
}

/**
 * Offset-paged merged email+meeting timeline as an infinite query ("Load 25 more").
 * Pages accumulate in the query cache — no component-side state/effects; a kind/id
 * change is a new queryKey, so the list resets for free.
 */
export function useCrmTimeline(
  kind: CrmEntityKind,
  id: number | null,
  limit = 25,
  filters?: { types?: string[]; before?: string | null },
) {
  // Timeline-v2 (E3): `types` (a subset of the activity taxonomy) + `before` (ISO date/datetime cutoff)
  // ride in the queryKey, so changing a filter is a NEW infinite query — paging resets for free.
  const types = filters?.types?.length ? filters.types.join(',') : undefined;
  const before = filters?.before || undefined;
  return useInfiniteQuery<
    CrmTimelineResponse,
    Error,
    InfiniteData<CrmTimelineResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: ['crm', 'timeline', kind, id, limit, types ?? null, before ?? null],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ offset: String(pageParam), limit: String(limit) });
      if (types) qs.set('types', types);
      if (before) qs.set('before', before);
      return apiFetch<CrmTimelineResponse>(`/api/crm/${kindPath(kind)}/${id}/timeline?${qs.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (last) => (last.has_more ? last.offset + last.rows.length : undefined),
    enabled: id != null,
    staleTime: 60_000,
  });
}

/** The Timeline-v2 filter vocabulary (§7.4) — the shared activity taxonomy's emittable types, fetched
 *  so the filter chips are backend-driven (finding 12), not a hardcoded label list. Static → cached long. */
export function useCrmActivityTypes() {
  return useQuery<CrmActivityTypesResponse>({
    queryKey: ['crm', 'activity-types'],
    queryFn: () => apiFetch<CrmActivityTypesResponse>('/api/crm/activity-types'),
    staleTime: 60 * 60_000,
  });
}

export function useCrmEmployment(personId: number | null) {
  return useQuery<CrmEmploymentResponse>({
    queryKey: ['crm', 'employment', personId],
    queryFn: () => apiFetch<CrmEmploymentResponse>(`/api/crm/person/${personId}/employment`),
    enabled: personId != null,
    staleTime: 5 * 60_000,
  });
}

export function useCrmOrgPeople(orgId: number | null) {
  return useQuery<CrmOrgPeopleResponse>({
    queryKey: ['crm', 'org-people', orgId],
    queryFn: () => apiFetch<CrmOrgPeopleResponse>(`/api/crm/organization/${orgId}/people`),
    enabled: orgId != null,
    staleTime: 5 * 60_000,
  });
}

export function useCrmConnections(kind: CrmEntityKind, id: number | null) {
  return useQuery<CrmConnectionsResponse>({
    queryKey: ['crm', 'connections', kind, id],
    queryFn: () =>
      apiFetch<CrmConnectionsResponse>(`/api/crm/${kindPath(kind)}/${id}/connections`),
    enabled: id != null,
    staleTime: 5 * 60_000,
  });
}

/** Entity-profile LINKED RECORDS — active relationship facts whose `to` endpoint is a list entry the
 * caller may see, grouped by list and ORDERED BY THE PAYLOAD (salience, then recency: the card never
 * re-sorts). Replaced `useCrmLinkedDeals`, whose heuristic `/deals` endpoint no longer exists. */
export function useCrmLinkedRecords(kind: CrmEntityKind, id: number | null) {
  return useQuery<CrmLinkedRecordsResponse>({
    queryKey: ['crm', 'linked-records', kind, id],
    queryFn: () =>
      apiFetch<CrmLinkedRecordsResponse>(`/api/crm/${kindPath(kind)}/${id}/linked-records`),
    enabled: id != null,
    staleTime: 5 * 60_000,
  });
}

export function useCrmEmailMeta(emailId: number | null) {
  return useQuery<CrmEmailMeta>({
    queryKey: ['crm', 'email', emailId, 'meta'],
    queryFn: () => apiFetch<CrmEmailMeta>(`/api/crm/email/${emailId}/meta`),
    enabled: emailId != null,
    staleTime: 5 * 60_000,
  });
}

/**
 * bcc-redacted participants for ONE message. Pillar F.2 uses this as the timeline row's
 * fetch-on-expand: `enabled` stays false until a row is opened, so a collapsed row costs nothing and
 * an expand/collapse/expand cycle costs ONE request (React Query serves the cached page after that).
 */
export function useCrmEmailParticipants(emailId: number | null, enabled = true) {
  return useQuery<CrmEmailParticipantsResponse>({
    queryKey: ['crm', 'email', emailId, 'participants'],
    queryFn: () => apiFetch<CrmEmailParticipantsResponse>(`/api/crm/email/${emailId}/participants`),
    enabled: enabled && emailId != null,
    staleTime: 5 * 60_000,
  });
}

/** Pillar F.2 — the calendar half of the reveal: organizer + attendees for ONE meeting, on expand. */
export function useCrmMeetingAttendees(meetingId: number | null, enabled = true) {
  return useQuery<CrmMeetingAttendeesResponse>({
    queryKey: ['crm', 'meeting', meetingId, 'attendees'],
    queryFn: () => apiFetch<CrmMeetingAttendeesResponse>(`/api/crm/meeting/${meetingId}/attendees`),
    enabled: enabled && meetingId != null,
    staleTime: 5 * 60_000,
    // Deliberate, not a copied default: this endpoint answers an existence-safe 404 for a meeting the
    // caller may not see, and the surface WRITES AN AUDIT ROW on that denied read. Under the client's
    // global `retry: 1`, one expand of a denied row logged the denial twice — noise in the one log that
    // has to stay readable (review minor 9). Matches useCrmPersonCard, which is 404-shaped for the
    // same reason.
    retry: false,
  });
}

/**
 * Pillar F.2 — the hovercard tile (name / title / firm) for ONE person.
 *
 * `enabled` is the whole point: the caller passes true only after the hover debounce has elapsed on a
 * LINKED (external, resolved) participant, so an internal or unresolved name never issues a request
 * and a mouse sweeping across a participant list issues none either. The cache is the per-session
 * memo the ruling asks for — a second hover over the same person re-renders from it with no fetch.
 * `retry: false` so a 404 (gated / merged-away) doesn't re-hit an audited endpoint three times.
 */
export function useCrmPersonCard(personId: number | null, enabled: boolean) {
  return useQuery<CrmPersonCard>({
    queryKey: ['crm', 'person', personId, 'card'],
    queryFn: () => apiFetch<CrmPersonCard>(`/api/crm/person/${personId}/card`),
    enabled: enabled && personId != null,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * The PRINCIPAL the governed content caches belong to.
 *
 * ⛔ A content query key that is only the message id is a cross-principal leak waiting to happen:
 * React Query caches per key, so "view as Alice" and then "view as Carol" would share one entry and
 * the second reader could be painted the first one's body or filenames before any request returns
 * (spec §3.3 — "query keys include authenticated/effective principal, view-as context and source
 * surface, never just message ID"). `useEffectiveUser` gives both halves: the REAL signed-in
 * account and the effective (view-as) identity, and BOTH belong in the key — an admin reading as
 * themselves and an admin reading as Alice are different audiences of the same door.
 *
 * Emails are lower-cased so a casing difference between MSAL's `username` and the picker's value
 * cannot silently fork the cache into two entries that each re-fetch.
 */
function useContentPrincipalKey(): ContentPrincipal {
  const { realEmail, effectiveEmail } = useEffectiveUser();
  return [(realEmail || '').toLowerCase(), (effectiveEmail || '').toLowerCase()];
}

/** `[realEmail, effectiveEmail]`, both lower-cased. In a signed-out context (and in jsdom, where
 *  there is no MSAL account) both are `''` — which is still a principal, and still shared by
 *  nobody else. */
export type ContentPrincipal = [string, string];

/** The governed-content query keys, EXPORTED so a test seeds the cache through the same builder the
 *  hook reads from. A test that hand-writes the array is a test that keeps passing after the key
 *  gains a segment — while the app quietly stops finding its own cache. */
export const crmEmailBodyKey = (emailId: number | null, principal: ContentPrincipal) =>
  ['crm', 'email', emailId, 'body', principal] as const;
export const crmEmailContentKey = (emailId: number | null, principal: ContentPrincipal) =>
  ['crm', 'email', emailId, 'content', principal] as const;

/**
 * The gated body read (two-step probe + bytes collapsed into one query). 403 (not on
 * thread) surfaces as the query's error — the component renders the "not on this
 * thread" state off `ApiError.status === 403`; retry is disabled so a denial doesn't
 * hammer the audit log.
 *
 * ⛔ **ZERO stale time, refetch on mount, and principal-keyed** (governed-email-content spec §3.3,
 * superseding the inline-message spec's five-minute allowance). Three separate changes, because
 * each closes a different hole and none of them alone is enough:
 *
 *   1. `staleTime: 0` + `refetchOnMount: 'always'` — a deliberate REOPEN must make a fresh
 *      authorized read. The old five-minute window meant a reader whose entitlement had been
 *      revoked kept seeing the body from cache, and — just as bad — that re-read wrote no audit
 *      row, so "audited per read" was really "audited per fetch".
 *   2. `gcTime: 0` — zero stale time alone is NOT sufficient, and neither was the
 *      `placeholderData: undefined` the first cut relied on: that is React Query's DEFAULT and
 *      does nothing, because the stale paint does not come from placeholder data at all. It comes
 *      from the CACHE ENTRY, which survives an unmount for `gcTime` (5 minutes by default) and is
 *      served instantly on the next mount while the re-gating request is still in flight — the
 *      "cached body flash before the denial lands" case, verbatim (acceptance G8, review MAJOR 2).
 *      `gcTime: 0` drops the entry the moment the last reader unmounts, so a reopen has nothing to
 *      paint and starts at `isLoading`. The ladders additionally treat an in-flight refetch as
 *      `loading`, which covers the case where the component never unmounted.
 *   3. The principal in the key (above) — so a login or view-as switch cannot land on the previous
 *      identity's entry at all (acceptance G9).
 */
export function useCrmEmailBody(emailId: number | null, enabled: boolean) {
  const principal = useContentPrincipalKey();
  return useQuery<string>({
    queryKey: crmEmailBodyKey(emailId, principal),
    queryFn: () => apiFetchText(`/api/crm/email/${emailId}/body/content`),
    enabled: emailId != null && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 0,
    retry: false,
  });
}

/**
 * EC-2 — the governed content STATUS for one email: body availability plus the stored-file list
 * and each file's state (spec §9 envelope).
 *
 * `enabled` is the whole point, and it is the no-prefetch rule in code: the caller passes `true`
 * only once a row is actually EXPANDED or a message view is open. A collapsed timeline row, a
 * hovercard and a page-level timeline fetch must never reach this door — one opened row fanning out
 * to every message on the page would be both an audit-log flood and a governed read nobody asked
 * for (spec §3.1).
 *
 * Same cache properties as the body above — `staleTime: 0`, `refetchOnMount: 'always'`,
 * `gcTime: 0`, the principal in the key — for the same reasons: FILENAMES are content, not
 * metadata ("Project Falcon CIM.pdf" is itself the disclosure), so they are cached exactly as
 * carefully as the body text, which in this build means not cached across an open at all.
 *
 * A 404 here means "nothing to show" — the feature flag is off, or the message is not visible. The
 * component renders nothing rather than an error, the `CrmLinkedRecords` precedent for a
 * flag-gated door.
 */
export function useCrmEmailContent(emailId: number | null, enabled: boolean) {
  const principal = useContentPrincipalKey();
  return useQuery<CrmEmailContent>({
    queryKey: crmEmailContentKey(emailId, principal),
    queryFn: () => apiFetch<CrmEmailContent>(`/api/crm/email/${emailId}/content`),
    enabled: emailId != null && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 0,
    retry: false,
  });
}

/** The identity of ONE open, for the acquisition loop's per-open state.
 *
 *  ⛔ **THE PRINCIPAL IS IN IT** (review MINOR 8). A view-as switch re-keys the queries, so the
 *  envelope is re-read for the new identity — but the loop's `attempted` set is keyed by the OPEN,
 *  and without the principal the surface that never unmounted still believes it has already tried
 *  this resource. The new principal would then sit on a `not_captured` body that nobody ever
 *  fetches, with no affordance to say so. It is the same reasoning as the query keys themselves:
 *  what was done on your behalf is not what was done on someone else's. */
export function useCrmContentOpenKey(prefix: string): string {
  return `${prefix}:${useContentPrincipalKey().join('>')}`;
}

/** THIS component's content-status query key, principal and all — so a surface can invalidate its
 *  own status read without rebuilding the key (and without a second, drifting copy of it). */
export function useCrmEmailContentKey(emailId: number | null) {
  return crmEmailContentKey(emailId, useContentPrincipalKey());
}

/** THIS component's BODY query key, principal and all.
 *
 *  ⛔ **THE ACQUISITION LOOP HAS TO BE ABLE TO INVALIDATE THE THING THAT DISPLAYS THE BODY.** The
 *  status envelope and the body are two different queries against two different doors, so a
 *  successful body acquisition settles the STATUS read and leaves the BODY read holding its 404 —
 *  which is what put "No stored body for this message (metadata only)" on screen in the Release B
 *  canary (msg 494083) minutes after the body had in fact been captured. The record surface passed
 *  its own body key from the start; the two email surfaces passed nothing.
 *
 *  It is a hook rather than a hand-built array on purpose: a key assembled at the call site is a
 *  key that keeps looking right after the real one gains a segment, while the app quietly
 *  invalidates nothing. Same reasoning as `useCrmEmailContentKey`. */
export function useCrmEmailBodyKey(emailId: number | null) {
  return crmEmailBodyKey(emailId, useContentPrincipalKey());
}

/** The RECORD surface's content door — list/entry/event, never a bare message id.
 *
 *  ⛔ A record surface must never substitute the generic email route to route around its own
 *  denial (spec §9), so every read it makes is built from this one expression. */
export const crmRecordEvidenceContentBase = (
  listId: number, entryId: number, eventLogId: number,
) => `/api/crm/lists/${listId}/entries/${entryId}/evidence/${eventLogId}/email-content`;

/** The record-context content status for ONE evidence row. Same envelope as the email door, so the
 *  shared list renders one story — plus the authorized event context the server echoes back.
 *
 *  `enabled` is the whole point, exactly as on the email surfaces: the caller passes true only once
 *  a row is EXPANDED. A collapsed record row, a hovercard and the page-level evidence fetch never
 *  reach this door, so nothing is prefetched and no paperclip count exists before authorization. */
export function useCrmRecordEvidenceContent(
  listId: number | null, entryId: number | null, eventLogId: number | null, enabled: boolean,
) {
  const principal = useContentPrincipalKey();
  return useQuery<CrmEmailContent>({
    queryKey: ['crm', 'record-evidence-content', listId, entryId, eventLogId, principal],
    queryFn: () => apiFetch<CrmEmailContent>(
      crmRecordEvidenceContentBase(listId!, entryId!, eventLogId!)),
    enabled: enabled && listId != null && entryId != null && eventLogId != null,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 0,
    retry: false,
  });
}

/** That query's key, for the surface's own invalidation (see `useCrmEmailContentKey`). */
export function useCrmRecordEvidenceContentKey(
  listId: number | null, entryId: number | null, eventLogId: number | null,
) {
  return ['crm', 'record-evidence-content', listId, entryId, eventLogId,
    useContentPrincipalKey()] as const;
}

/** The generic email surface's content door. One expression, so the status read, the POST and the
 *  byte read can never drift onto three different routes. */
export const crmEmailContentBase = (emailId: number) => `/api/crm/email/${emailId}/content`;

/**
 * EC-2 — fetch ONE attachment's bytes through the authenticated client, as a Blob.
 *
 * ⛔ NOT a `useQuery`. These bytes are never cached: an object URL minted from a cached Blob
 * outlives the authorization that produced it, and the guarantee this build makes is a fresh gate
 * on every open. It is a plain async function the caller invokes from a click, and the caller owns
 * revoking whatever object URL it creates (spec §3.3, §9).
 *
 * A bearer token cannot ride on an `<img>`/`<iframe>`/`<a download>` URL, which is precisely why
 * the bytes come back as a Blob through `apiFetchBlob` rather than as a link the browser resolves.
 */
export async function fetchCrmAttachmentBlob(
  emailId: number, attachmentId: number, disposition: 'inline' | 'attachment',
  /**
   * EC-6 — the surface's OWN byte door. Defaults to the generic email route; the record timeline
   * passes its record-context route instead.
   *
   * ⛔ It is a parameter rather than a lookup because the rule is negative: a DENIED record route
   * must never be worked around by calling the generic email route (spec §9). Making the door an
   * input means a surface can only read through the door it was given.
   */
  base?: string,
): Promise<Blob> {
  const url = base
    ? `${base}/attachments/${attachmentId}/content`
    : `/api/crm/email/${emailId}/attachments/${attachmentId}/content`;
  return apiFetchBlob(`${url}?disposition=${disposition}`);
}

// ── Item E (E1): CRM Notes ──────────────────────────────────────────────────────────────────────
// Every note write invalidates BOTH the entity note list (previews/ordering) AND the single-note
// cache; a link/visibility change can move a note's ceiling → which entities show it → so the whole
// ['crm','notes'] namespace is invalidated on every mutation (correctness over surgical precision).
const invalidateNotes = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['crm', 'notes'] });

/** Readable notes linked to an entity (newest-first), for the profile Notes section.
 *
 * ⛔ `enabled` IS AN OPTION AND MUST NEVER ENTER THE QUERY KEY (E1b-3 S4, spec D1). This key is
 * byte-shared with `CrmNotesSection`'s on purpose: both surfaces read the same governed, AUDITED
 * note list, and a forked key meant a second fetch and with it a second `crm_access_audit`
 * `note_view` row — audit noise that misleads whoever later reconstructs who read what. Deferral is
 * a statement about WHEN this caller asks, not about WHAT it asks for, so it belongs beside
 * `staleTime`, never in the identity of the cache entry. `limit` is in the key because it does
 * change the request; that is also why neither caller passes one.
 *
 * The options are an object rather than two positional args for the same reason: `(kind, id, 20,
 * false)` is one transposition away from forking the key by accident. */
export function useEntityNotes(
  targetKind: string, targetId: number | null,
  { limit = 20, enabled = true }: { limit?: number; enabled?: boolean } = {},
) {
  return useQuery<CrmNoteListResponse>({
    queryKey: ['crm', 'notes', 'list', targetKind, targetId, limit],
    queryFn: () => apiFetch<CrmNoteListResponse>(
      `/api/crm/notes?target_kind=${encodeURIComponent(targetKind)}&target_id=${targetId}&limit=${limit}`),
    enabled: enabled && targetId != null,
  });
}

/** One full note (the overlay). */
export function useNote(noteId: number | null) {
  return useQuery<CrmNote>({
    queryKey: ['crm', 'notes', 'one', noteId],
    queryFn: () => apiFetch<CrmNote>(`/api/crm/notes/${noteId}`),
    enabled: noteId != null,
    retry: false,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      body_json: unknown;
      visibility_mode?: string;
      links?: { target_kind: string; target_id: number }[];
    }) => apiFetch<CrmNote>('/api/crm/notes', { method: 'POST', body: JSON.stringify(body) }),
    onSettled: () => invalidateNotes(qc),
  });
}

export function usePatchNote() {
  const qc = useQueryClient();
  return useMutation({
    // 409 stale_note surfaces as an ApiError the caller inspects (detail.current_revision).
    mutationFn: ({ noteId, ...body }: {
      noteId: number; expected_revision: number; body_json?: unknown; visibility_mode?: string;
    }) => apiFetch<CrmNote | { id: number; deleted: true }>(
      `/api/crm/notes/${noteId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSettled: () => invalidateNotes(qc),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: number) =>
      apiFetch<{ deleted: boolean }>(`/api/crm/notes/${noteId}`, { method: 'DELETE' }),
    onSettled: () => invalidateNotes(qc),
  });
}

export function useAddNoteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, target_kind, target_id }:
                 { noteId: number; target_kind: string; target_id: number }) =>
      apiFetch<CrmNote>(`/api/crm/notes/${noteId}/links`,
        { method: 'POST', body: JSON.stringify({ target_kind, target_id }) }),
    onSettled: () => invalidateNotes(qc),
  });
}

export function useRemoveNoteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, linkId }: { noteId: number; linkId: number }) =>
      apiFetch<CrmNote>(`/api/crm/notes/${noteId}/links/${linkId}`, { method: 'DELETE' }),
    onSettled: () => invalidateNotes(qc),
  });
}

/** Note link/mention lookup — person/org/opportunity via the shared /crm/lookup endpoint. */
export function useNoteLinkLookup(kind: string, q: string) {
  const query = q.trim();
  return useQuery<CrmLookupResponse>({
    queryKey: ['crm', 'notes', 'lookup', kind, query],
    queryFn: () => apiFetch<CrmLookupResponse>('/api/crm/lookup', {
      method: 'POST', body: JSON.stringify({ kind, q: query, limit: 10 }),
    }),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

// ── PM-9: the entity-profile merge modal (person-merge spec §7) ──────────────
// The two GET hooks are reads; the two mutations call the SHIPPED merge / undo routes with their own
// ritual intact (preview by default, evidence note required, typed confirmation). Nothing here
// re-implements a merge.

/** Counterpart picker: the record itself, the duplicate finder's suggestions, and — once `q` is two
 *  characters — governed search results of the same kind. Admin-only server-side. */
export function useCrmMergeCandidates(kind: CrmEntityKind, id: number, q: string, enabled: boolean) {
  const query = q.trim();
  return useQuery<CrmMergeCandidatesResponse>({
    queryKey: ['crm', 'merge-candidates', kind, id, query],
    queryFn: () => apiFetch<CrmMergeCandidatesResponse>(
      `/api/crm/merge-candidates/${kind}/${id}?q=${encodeURIComponent(query)}`),
    enabled,
    staleTime: 15_000,
  });
}

/** The reversible merge-log row for this record + whether an undo is eligible and, if not, WHY —
 *  so the affordance is disabled with its reason instead of failing on click. */
export function useCrmMergeHistory(kind: CrmEntityKind, id: number, enabled: boolean) {
  return useQuery<CrmMergeHistoryResponse>({
    queryKey: ['crm', 'merge-history', kind, id],
    queryFn: () => apiFetch<CrmMergeHistoryResponse>(`/api/crm/merge-history/${kind}/${id}`),
    enabled,
    staleTime: 15_000,
  });
}

/** The merge preview / apply. `confirm_loser_id` absent or mismatched returns the PREVIEW and writes
 *  nothing — that is the route's own structural gate, not something this hook adds or can bypass. */
export function useCrmMerge(kind: CrmEntityKind) {
  const qc = useQueryClient();
  const path = kind === 'person' ? 'admin/person-merge' : 'admin/org-merge';
  return useMutation({
    mutationFn: (body: {
      survivor_id: number; loser_id: number; evidence_note?: string | null;
      confirm_loser_id?: number | null; acknowledge_governance_impact?: boolean;
    }) => apiFetch<Record<string, unknown>>(`/api/crm/${path}`,
      { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (out, body) => {
      if (!out || out.applied !== true) return;   // a preview invalidates nothing
      for (const eid of [body.survivor_id, body.loser_id]) {
        qc.invalidateQueries({ queryKey: ['crm', 'profile', kind, eid] });
        qc.invalidateQueries({ queryKey: ['crm', 'merge-history', kind, eid] });
      }
      qc.invalidateQueries({ queryKey: ['crm', 'merge-candidates'] });
    },
  });
}

/** The merge undo, same shape: `confirm_merge_log_id` is the arm, and the undo has no undo. */
export function useCrmMergeUndo(kind: CrmEntityKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      merge_log_id: number; reason_note?: string | null; confirm_merge_log_id?: number | null;
    }) => apiFetch<Record<string, unknown>>('/api/crm/admin/merge-undo',
      { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (out) => {
      if (!out || out.undone !== true) return;
      qc.invalidateQueries({ queryKey: ['crm', 'profile', kind] });
      qc.invalidateQueries({ queryKey: ['crm', 'merge-history', kind] });
      qc.invalidateQueries({ queryKey: ['crm', 'merge-candidates'] });
    },
  });
}
